import { Lightning, Colors } from '@lightningjs/sdk';
import { formatVideoTime, getVideoTimeTts } from 'support/contentUtils';
import { translate } from 'support/translate';
import { StateEvent } from 'types/lightning';
import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';
import {
  debounce,
  getImageTextureObj,
  debounceAnnounce,
  preloadTextures,
} from 'support/generalUtils';
import { constants } from 'aliases';
import Button from 'components/common/buttons/Button';
import {
  CcAudioButton,
  CommonPlaybackControls,
  DEFAULT_SEEK_RATE,
  DEFAULT_SEEK_SPEED,
  PAUSE_ICON,
  PLAY_ICON,
  SCRUB_DELAY,
  SCRUB_RATE,
  SKIP_INTERVAL,
} from './CommonPlaybackControls';
import VodProgressBar from 'components/pages/playback/overlay/ProgressBar/VodProgressBar';
import {
  HoverableComponent,
  HoverableComponentSignalMap,
} from 'components/common/HoverableComponent';
import FocusableIcon from 'components/common/FocusableIcon';

const CONTROL_W = 1580;
const CONTROL_H = 252;

const CC_CONTAINER_H = 48;

const PROGRESS_BAR_WIDTH = constants.ui.progressBarWidth;

const CONTROLS_CONTAINER_H = 73;
const CONTROLS_CONTAINER_Y = 42;

const FONT_SIZE = 29;

const ICON_SIZE = 30;
const START_OVER_BUTTON_W = 179;
const START_OVER_ICON = getImageTextureObj(
  'static/images/playback/start-over-icon.svg',
  ICON_SIZE,
  ICON_SIZE,
);

const FOCUSED_ALPHA = 1;
const UNFOCUSED_ALPHA = 0.5;
const MARGIN_MEDIUM = 16;

interface VodControlsTemplateSpec extends Lightning.Component.TemplateSpec {
  duration: number;
  progress: number;
  seekRate: number;
  seekSpeed: number;
  finalSeekPosition: number | null;
  isAdPlaying: boolean;
  adCuePoints: AdCuePoint[];
  onAnnounceStarted?: () => void;
  onAnnounceEnded?: () => void;

  CcContainer: {
    CcButton: typeof FocusableIcon;
  };
  ProgressBar: typeof VodProgressBar;
  ControlsContainer: {
    StartOver: typeof Button;
    PlaybackControls: typeof CommonPlaybackControls;
  };
}

interface VodControlsSignalMap extends HoverableComponentSignalMap {
  skip(increment: number): void;
  seek(position: number): void;
  seekPause(): void;
  seekEnd(): void;
  pause(): void;
  play(): void;
  seeking(): void;
  startOver(): void;
  ccAudio(): void;
  onControlHover(): void;
}

interface VodControlsTypeConfig extends Lightning.Component.TypeConfig {
  SignalMapType: VodControlsSignalMap;
}

export default class VodControls
  extends HoverableComponent<VodControlsTemplateSpec, VodControlsTypeConfig>
  implements Lightning.Component.ImplementTemplateSpec<VodControlsTemplateSpec>
{
  private _duration = 0;
  private _progress = 0;
  private _seekRate = DEFAULT_SEEK_RATE;
  private _seekSpeed = DEFAULT_SEEK_SPEED;
  private _finalSeekPosition: VodControlsTemplateSpec['finalSeekPosition'] =
    null;
  private _isVideoPlaying = false;
  private _isAdPlaying = false;
  private _adCuePoints: VodControlsTemplateSpec['adCuePoints'] = [];

  private _savedState = '';

  private _CcContainer = this.getByRef('CcContainer')!;
  private _CcAudio = this._CcContainer.getByRef('CcButton')!;
  private _ProgressBar = this.getByRef('ProgressBar')!;
  private _ControlsContainer = this.getByRef('ControlsContainer')!;
  private _StartOver = this._ControlsContainer.getByRef('StartOver')!;
  private _PlaybackControls =
    this._ControlsContainer.getByRef('PlaybackControls')!;
  private _SkipBackward = this._PlaybackControls.getByRef('SkipBackward')!;
  private _PlayPauseIcon = this._PlaybackControls.getByRef('PlayPauseIcon')!;
  private _SkipForward = this._PlaybackControls.getByRef('SkipForward')!;

  private hasScrubbed = false;
  private initialPlayPauseState:
    | keyof Pick<VodControlsSignalMap, 'play' | 'pause'>
    | null = null;
  private scrubPosition: null | number = null;

  private finishScrubTimeout: null | (() => void) = null;
  private prevState = '';

  get progress() {
    return this._progress;
  }

  set progress(progress: VodControlsTemplateSpec['progress']) {
    this._progress = progress;
    this._ProgressBar.progress = this.finalSeekPosition ?? this.progress;
  }

  get duration() {
    return this._duration;
  }

  set duration(duration: VodControlsTemplateSpec['duration']) {
    this._duration = duration;
    this._ProgressBar.duration = duration;
    this._ProgressBar.progress = this.finalSeekPosition ?? this.progress;
  }

  get seekRate() {
    return this._seekRate;
  }

  set seekRate(seekRate: VodControlsTemplateSpec['seekRate']) {
    this._seekRate = seekRate;
    this._ProgressBar.seekRate = seekRate;
  }

  get seekSpeed() {
    return this._seekSpeed;
  }

  set seekSpeed(seekSpeed: VodControlsTemplateSpec['seekSpeed']) {
    this._seekSpeed = seekSpeed;
    this._ProgressBar.seekSpeed = seekSpeed;
    this._ProgressBar.progress = this.finalSeekPosition ?? this.progress;
  }

  get finalSeekPosition() {
    return this._finalSeekPosition;
  }

  set finalSeekPosition(
    finalSeekPosition: VodControlsTemplateSpec['finalSeekPosition'],
  ) {
    this._finalSeekPosition = finalSeekPosition;
    if (finalSeekPosition === null) return;

    this._ProgressBar.progress = finalSeekPosition;
  }

  get isAdPlaying() {
    return this._isAdPlaying;
  }

  set isAdPlaying(isAdPlaying: VodControlsTemplateSpec['isAdPlaying']) {
    this._isAdPlaying = isAdPlaying;
    this.shouldShowAdControls(isAdPlaying);
    if (isAdPlaying) this._setState('PlayPause');
  }

  get adCuePoints() {
    return this._adCuePoints;
  }

  set adCuePoints(adCuePoints: VodControlsTemplateSpec['adCuePoints']) {
    this._adCuePoints = adCuePoints;
    this._ProgressBar.adCuePoints = adCuePoints;
  }

  static override _template(): Lightning.Component.Template<VodControlsTemplateSpec> {
    return {
      w: CONTROL_W,
      h: CONTROL_H,
      flex: {
        direction: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      },
      CcContainer: {
        w: CONTROL_W,
        h: CC_CONTAINER_H,
        flex: {
          justifyContent: 'flex-end',
        },
        CcButton: {
          ...CcAudioButton,
          signals: {
            $ccAudio: true,
            $onHover: '$onHover',
          },
        },
      },
      ProgressBar: {
        type: VodProgressBar,
        signals: {
          $onHover: '$onHover',
          $onCursorClick: '$onCursorClick',
        },
      },
      ControlsContainer: {
        w: CONTROL_W,
        h: CONTROLS_CONTAINER_H,
        y: CONTROLS_CONTAINER_Y,
        StartOver: {
          type: Button,
          minWidth: START_OVER_BUTTON_W,
          height: CONTROLS_CONTAINER_H,
          margin: 0,
          action: '$startOver',
          signals: { $startOver: true, $onHover: '$onHover' },
          reportClick: false,
          backgroundColor: {
            focused: Colors('transparent').get(),
            unfocused: Colors('transparent').get(),
          },
          fontColor: {
            focused: Colors('text').get(),
            unfocused: Colors('text').alpha(UNFOCUSED_ALPHA).get(),
          },
          fontSize: FONT_SIZE,
          label: translate('playback.startOver'),
          startIcon: {
            focused: {
              ...START_OVER_ICON,
              alpha: 1,
              flexItem: { marginRight: MARGIN_MEDIUM },
            },
            unfocused: {
              ...START_OVER_ICON,
              alpha: UNFOCUSED_ALPHA,
              flexItem: { marginRight: MARGIN_MEDIUM },
            },
          },
        },
        PlaybackControls: {
          type: CommonPlaybackControls,
          x: PROGRESS_BAR_WIDTH / 2,
          h: CONTROLS_CONTAINER_H,
          signals: {
            SkipBack: '$skipPlayback',
            PlayPause: '$handlePlayPause',
            SkipForward: '$skipPlayback',
            onHover: '$onHover',
          },
        },
      },
    };
  }

  override _setup() {
    this._setState('PlayPause');
  }

  override _disable() {
    this._setState('PlayPause');
  }

  override _focus() {
    this._setState(this._savedState);
  }

  override _unfocus() {
    this._setState('');
  }

  override _handleMediaPlay() {
    if (!this._isVideoPlaying) {
      this.finishSeeking('play');
    }

    return false;
  }

  override _handleMediaPause() {
    if (this.isSeeking()) this.finishSeeking('pause');
    this._setState('PlayPause');

    return false;
  }

  preloadIcons() {
    this._PlaybackControls.preloadIcons();
    preloadTextures(this, [START_OVER_ICON]);
  }

  private saveState() {
    this._savedState = this._getState();
  }

  private shouldShowAdControls(show: boolean) {
    this._StartOver.visible = !show;
    this._PlaybackControls.setStepButtonsVisibility(!show);

    show
      ? this._ProgressBar.hideAdPodMarkers()
      : this._ProgressBar.showAdPodMarkers();
  }

  private $handlePlayPause() {
    if (this._isVideoPlaying) {
      this.signal('pause');
    } else {
      this.signal('play');
    }
  }

  private $startOver() {
    this.signal('startOver');
  }

  private $ccAudio() {
    this.signal('ccAudio');
  }

  private $skipPlayback() {
    switch (this._getState()) {
      case 'SkipBackward':
        this.signal('skip', -SKIP_INTERVAL);
        break;
      case 'SkipForward':
        this.signal('skip', SKIP_INTERVAL);
        break;
    }
  }

  private $onHover(target: Lightning.Component) {
    switch (target) {
      case this._ProgressBar.playbackProgressBar:
        this._setState('ProgressBar');
        break;
      case this._StartOver:
        this._setState('StartOver');
        break;
      case this._PlayPauseIcon:
        this._setState('PlayPause');
        break;
      case this._SkipBackward:
        this._setState('SkipBackward');
        break;
      case this._SkipForward:
        this._setState('SkipForward');
        break;
      case this._CcAudio:
        this._setState('CcAudio');
        break;
    }
    // Reset overlay idle timer on hover
    this.signal('onControlHover');
  }

  onRewind() {
    if (this._isAdPlaying) return;
    this._setState('ProgressBar');
    if (this._isVideoPlaying) this.signal('pause');
    this._PlaybackControls.onRewind();
  }

  onFastForward() {
    if (this._isAdPlaying) return;
    this._setState('ProgressBar');
    if (this._isVideoPlaying) this.signal('pause');
    this._PlaybackControls.onFastForward();
  }

  updateFromPause() {
    this._PlayPauseIcon.icon = PLAY_ICON;
    this._PlayPauseIcon.title = translate('ttsPrompts.playButton');
    this._isVideoPlaying = false;
  }

  updateFromPlay() {
    this._PlayPauseIcon.icon = PAUSE_ICON;
    this._PlayPauseIcon.title = translate('ttsPrompts.pauseButton');
    this._isVideoPlaying = true;
  }

  isSeeking() {
    return this.seekRate !== 1;
  }

  private finishSeeking(forceState?: 'play' | 'pause') {
    if (this.finalSeekPosition !== null) {
      this.signal('seek', this.finalSeekPosition);
    }
    if (forceState) {
      this.signal(forceState);
    } else if (this.initialPlayPauseState) {
      this.signal(this.initialPlayPauseState);
    }
    this.resetSeeking();
    this.signal('seekEnd');
  }

  private resetSeeking() {
    this.seekRate = DEFAULT_SEEK_RATE;
    this.seekSpeed = DEFAULT_SEEK_SPEED;
    this.finalSeekPosition = null;
    this.initialPlayPauseState = null;
    this.scrubPosition = null;

    this._PlaybackControls.clearSeekInterval();
    this._PlaybackControls.clearSeekAnnounceInterval();
  }

  static override _states() {
    return [
      class ProgressBar extends this {
        private setupSeeking() {
          this._PlaybackControls.getSeekRate = () => this.seekRate;
          this._PlaybackControls.getSeekSpeed = () => this.seekSpeed;

          this._PlaybackControls.getInitialPlayPauseState = () => {
            if (!this.initialPlayPauseState) {
              this.initialPlayPauseState = this._isVideoPlaying
                ? 'play'
                : 'pause';
            }
            this._PlaybackControls.clearSeekAnnounceInterval();
          };

          this._PlaybackControls.setRateSpeed = (newSeekRate, newSeekSpeed) => {
            this.seekRate = newSeekRate;
            this.seekSpeed = newSeekSpeed;
            this._PlaybackControls.setupAnnounceInterval(this);
          };

          this._PlaybackControls.finishSeeking = this.finishSeeking.bind(this);
          this._PlaybackControls.onIncrementSeek =
            this.onIncrementSeek.bind(this);
        }

        override $enter(event: StateEvent) {
          this.setupSeeking();
          this.saveState();
          this.hasScrubbed = false;
          if (this._isAdPlaying) {
            this._setState('PlayPause'); // prevent entering state when ad is playing
            return;
          }

          if (event.prevState !== 'CcAudio') {
            this.prevState = event.prevState;
          }

          this.finishScrubTimeout = debounce(
            this.finishSeeking.bind(this),
            SCRUB_DELAY,
          );
        }

        override $exit() {
          this.cleanup();
        }

        $onCursorClick(
          target: Lightning.Component,
          localCoords: { x: number; y: number },
        ) {
          // If progressBar is clicked, seek to target progress.
          if (target === this._ProgressBar.playbackProgressBar) {
            const targetPoint =
              localCoords.x / this._ProgressBar.playbackProgressBar.w;
            const seekTarget = targetPoint * this.duration;
            this.signal('seek', seekTarget);

            // Reset overlay idle timer on hover after seeking.
            this.signal('onControlHover');
          }
        }

        override _getFocused() {
          return this._ProgressBar;
        }

        override _inactive() {
          this._setState('PlayPause');
          this.cleanup();
        }

        override _handleDown() {
          if (this.isSeeking()) this._handleEnter();
          this._setState(this.prevState || 'PlayPause');
        }

        override _handleLeft() {
          this.scrub('backwards');
        }

        override _handleRight() {
          this.scrub('forwards');
        }

        override _handleEnter() {
          if (!this._isVideoPlaying) {
            this.finishSeeking();
          }
          this.$handlePlayPause();
        }

        override _handleBack() {
          if (!this.isSeeking()) return false;
          this._handleEnter();
        }

        override _handleUp() {
          this._setState('CcAudio');
        }

        private scrubAnnounce(position: number) {
          const time = getVideoTimeTts(formatVideoTime(position));
          debounceAnnounce(this, time);
        }

        private scrub(direction: 'forwards' | 'backwards') {
          if (!this.initialPlayPauseState) {
            this.initialPlayPauseState = this._isVideoPlaying
              ? 'play'
              : 'pause';
          }
          this.signal('seekPause');

          const scrubIncrement =
            direction === 'forwards' ? SCRUB_RATE : -SCRUB_RATE;

          const nextScrubPosition =
            (this.scrubPosition ?? this.progress) + scrubIncrement;

          if (nextScrubPosition < 0) {
            this.scrubPosition = 0;
          } else if (nextScrubPosition >= this.duration) {
            this.scrubPosition = this.duration;
          } else {
            this.scrubPosition = nextScrubPosition;
          }

          // cancel previous TTS before announcing scrub positions (cancels progress bar focus announce)
          if (!this.hasScrubbed) {
            this.fireAncestors('$announcerCancel');
            this.hasScrubbed = true;
          }
          this.scrubAnnounce(this.scrubPosition);

          this.finishScrubTimeout?.();
          this.finalSeekPosition = this.scrubPosition;
        }

        private onIncrementSeek() {
          this.signal('seeking');

          if (this.finalSeekPosition === null) {
            this.finalSeekPosition = this.progress;
          }
          this.finalSeekPosition += this.seekRate;

          // Play video once user reaches either the start or end of video while seeking
          if (
            this.finalSeekPosition < 0 ||
            this.finalSeekPosition >= this.duration
          ) {
            this.finishSeeking();
            this.signal('play');
          }
        }

        private cleanup() {
          this.resetSeeking();
          this._ProgressBar.progress = this.progress;
        }
      },

      class StartOver extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._StartOver;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleRight() {
          this._setState('SkipBackward');
        }
      },

      class SkipBackward extends this {
        override $enter() {
          this.saveState();
          this._SkipBackward.smooth = { alpha: FOCUSED_ALPHA };
        }

        override $exit() {
          this._SkipBackward.smooth = { alpha: UNFOCUSED_ALPHA };
        }

        override _getFocused() {
          return this._SkipBackward;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleLeft() {
          this._setState('StartOver');
        }

        override _handleRight() {
          this._setState('PlayPause');
        }
      },

      class PlayPause extends this {
        override $enter() {
          this.saveState();
          this._PlayPauseIcon.smooth = { alpha: FOCUSED_ALPHA };
        }

        override $exit() {
          this._PlayPauseIcon.smooth = { alpha: UNFOCUSED_ALPHA };
        }

        override _getFocused() {
          return this._PlayPauseIcon;
        }

        override _handleUp() {
          if (this._isAdPlaying) this._setState('CcAudio');
          else this._setState('ProgressBar');
        }

        override _handleLeft() {
          if (this._isAdPlaying) return;
          this._setState('SkipBackward');
        }

        override _handleRight() {
          if (!this._isAdPlaying) this._setState('SkipForward');
        }
      },

      class SkipForward extends this {
        override $enter() {
          this.saveState();
          this._SkipForward.smooth = { alpha: FOCUSED_ALPHA };
        }

        override $exit() {
          this._SkipForward.smooth = { alpha: UNFOCUSED_ALPHA };
        }

        override _getFocused() {
          return this._SkipForward;
        }

        override _handleUp() {
          this._setState('ProgressBar');
        }

        override _handleLeft() {
          this._setState('PlayPause');
        }
      },
      class CcAudio extends this {
        override $enter() {
          this.saveState();
        }

        override _getFocused() {
          return this._CcAudio;
        }

        override _handleDown() {
          if (this._isAdPlaying) this._setState('PlayPause');
          else this._setState('ProgressBar');
        }
      },
    ];
  }
}
